/**
 * Passport review — shared hook that determines which critical documents
 * the consumer should upload (missing) or refresh (overdue).
 *
 * Powers both the Wallet Home "Outstanding journeys" section and the
 * Trust Wallet nudge banner so both surfaces stay in sync.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { loadWallet } from "@/lib/wallet-storage";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/** Category → months after which we ask the consumer to review the doc. */
export const REVIEW_RULES = [
  { category: "will",             months: 24, label: "Will",            critical: true },
  { category: "life_insurance",   months: 12, label: "Life Insurance",  critical: true },
  { category: "risk_cover",       months: 12, label: "Risk Cover",      critical: true },
  { category: "pension",          months: 12, label: "Pension",         critical: true },
  { category: "other_insurance",  months: 24, label: "Other Insurance", critical: false },
];

function monthsAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const now = new Date();
  return (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
}

export function formatAge(months) {
  if (months == null) return "";
  if (months >= 12) return `${Math.floor(months / 12)}y ${months % 12}m`;
  return `${months}m`;
}

export function usePassportReviewItems() {
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;
  const [state, setState] = useState({ overdue: [], missing: [], loaded: false });

  useEffect(() => {
    if (!deviceId) { setState(s => ({ ...s, loaded: true })); return; }
    axios.get(`${API}/wallet/documents`, { params: { device_id: deviceId } })
      .then(r => {
        const docs = r.data.documents || [];
        const byCat = new Map();
        for (const d of docs) {
          if (!byCat.has(d.category)) byCat.set(d.category, []);
          byCat.get(d.category).push(d);
        }
        const overdue = [];
        const missing = [];
        for (const rule of REVIEW_RULES) {
          const items = byCat.get(rule.category) || [];
          if (items.length === 0) {
            if (rule.critical) missing.push({ category: rule.category, label: rule.label });
            continue;
          }
          items.sort((a, b) => (b.document_date || b.uploaded_at || "").localeCompare(a.document_date || a.uploaded_at || ""));
          const latest = items[0];
          const ref = latest.document_date || latest.uploaded_at;
          const age = monthsAgo(ref);
          if (age != null && age >= rule.months) {
            overdue.push({
              category: rule.category,
              label: rule.label,
              age_months: age,
              document_id: latest.document_id,
              filename: latest.filename,
              institution: latest.institution_name,
            });
          }
        }
        setState({ overdue, missing, loaded: true });
      })
      .catch(() => setState(s => ({ ...s, loaded: true })));
  }, [deviceId]);

  return state;
}
